import { Portal } from '@angular/cdk/portal';
import { Injectable, signal } from '@angular/core';

export type FcpDrawerSize = 'small' | 'medium' | 'large' | 'full-screen';
export type FcpDrawerPosition = 'left' | 'right';

@Injectable({
    providedIn: 'root'
})
export class DrawerService {
    readonly contentPortal = signal<Portal<unknown> | null>(null);
    readonly drawerVisible = signal<boolean>(false);
    readonly drawerSize = signal<FcpDrawerSize>('medium');
    readonly drawerPosition = signal<FcpDrawerPosition>('right');
    readonly showCloseIcon = signal<boolean>(true);
    readonly e2eHook = signal<string | null>(null);

    setE2eHook(e2eHook: string | null): void {
        this.e2eHook.set(e2eHook);
    }

    setDrawerSize(size: FcpDrawerSize): void {
        this.drawerSize.set(size);
    }

    setDrawerPosition(position: FcpDrawerPosition): void {
        this.drawerPosition.set(position);
    }

    setShowCloseIcon(show: boolean): void {
        this.showCloseIcon.set(show);
    }

    setContentPortal(portal: Portal<unknown> | null): void {
        this.disposePortal(this.contentPortal());
        this.contentPortal.set(portal);
    }

    open(): void {
        this.drawerVisible.set(true);
    }

    /**
     * Clears any previous content, applies the given config, and opens the drawer.
     * Use this to show different content each time; content is also cleared when the drawer is closed.
     */
    openWithConfig(config: DrawerConfig): void {
        this.clearContent();
        this.applyConfig(config);
        this.drawerVisible.set(true);
    }

    /** Closes the drawer and clears projected content so the next open shows fresh content. */
    close(): void {
        this.clearContent();
        this.drawerVisible.set(false);
    }

    clear(): void {
        this.clearContent();
        this.drawerVisible.set(false);
    }

    /** Clears the portal and resets config state without changing visibility. */
    private clearContent(): void {
        this.disposePortal(this.contentPortal());
        this.contentPortal.set(null);
        this.drawerSize.set('medium');
        this.drawerPosition.set('right');
        this.showCloseIcon.set(true);
        this.e2eHook.set(null);
    }

    private applyConfig(config: DrawerConfig): void {
        if (config.size !== undefined) this.drawerSize.set(config.size);
        if (config.position !== undefined) this.drawerPosition.set(config.position);
        if (config.showCloseIcon !== undefined) this.showCloseIcon.set(config.showCloseIcon);
        if (config.e2eHook !== undefined) this.e2eHook.set(config.e2eHook);
        if (config.contentPortal !== undefined) this.setContentPortal(config.contentPortal);
    }

    private disposePortal(portal: Portal<unknown> | null): void {
        if (portal && portal.isAttached) {
            portal.detach();
        }
    }
}

export interface DrawerConfig {
    size?: FcpDrawerSize;
    position?: FcpDrawerPosition;
    showCloseIcon?: boolean;
    e2eHook?: string | null;
    contentPortal?: Portal<unknown> | null;
}
